/**
 * Staff admin accounts CRUD API.
 */

import { XLSX_MIME } from '@/constants/mime-types.constants';
import { apiClient } from './api-client';
import type { Admin, CreateAdminDto, UpdateAdminDto } from '@/types/admin.types';
import type { WithPhoneSetupLink } from '@/types/user.types';
import type { PaginatedResponse } from '@/types/data-table.types';
import type { GetUsersParams, UserWithProfile } from '@/types/user.types';
import { toLocalDateString } from '@/utils/format-date-utils';

/** `POST /users/invite` — create a staff admin. */
export const createAdmin = async (dto: CreateAdminDto): Promise<Admin> => {
  const response = await apiClient.post('/users/invite', dto);
  return response.data.user;
};

/** `PATCH /users/:id` — update a staff admin. */
export const updateAdmin = async (
  id: string,
  dto: UpdateAdminDto,
): Promise<WithPhoneSetupLink<Admin>> => {
  const response = await apiClient.patch<WithPhoneSetupLink<Admin>>(`/users/${id}`, dto);
  return response.data;
};

/** `GET /users/admins` — paginated admins. */
export const getAdmins = async (
  params?: GetUsersParams,
): Promise<PaginatedResponse<UserWithProfile>> => {
  const queryParams = new URLSearchParams();
  if (params?.search) queryParams.append('search', params.search);
  if (params?.page) queryParams.append('page', params.page.toString());
  if (params?.pageSize) queryParams.append('pageSize', params.pageSize.toString());
  if (params?.excludeIds?.length) queryParams.append('excludeIds', params.excludeIds.join(','));

  params?.statuses?.forEach((s) => queryParams.append('statuses', s));
  params?.roleCodes?.forEach((code) => queryParams.append('roleCodes', code));

  if (params?.establishmentId) queryParams.append('establishmentId', params.establishmentId);
  params?.programIds?.forEach((id) => queryParams.append('programIds', id));
  params?.departmentCodes?.forEach((code) => queryParams.append('departmentCodes', code));
  if (params?.dateFrom) queryParams.append('dateFrom', toLocalDateString(params.dateFrom));
  if (params?.dateTo) queryParams.append('dateTo', toLocalDateString(params.dateTo));

  const { data } = await apiClient.get(`/users/admins?${queryParams.toString()}`);
  return data;
};

/** `GET /users/admins/:identifier` */
export const getAdmin = async (identifier: string): Promise<UserWithProfile> => {
  const { data } = await apiClient.get(`/users/admins/${encodeURIComponent(identifier)}`);
  return data;
};

/** `GET /users/admins/export` — download XLSX. */
export const exportAdminsExcel = async (
  params?: Pick<
    GetUsersParams,
    | 'search'
    | 'statuses'
    | 'roleCodes'
    | 'dateFrom'
    | 'dateTo'
    | 'establishmentId'
    | 'programIds'
    | 'departmentCodes'
  >,
): Promise<void> => {
  const queryParams = new URLSearchParams();
  if (params?.search) queryParams.append('search', params.search);
  params?.statuses?.forEach((s) => queryParams.append('statuses', s));
  params?.roleCodes?.forEach((code) => queryParams.append('roleCodes', code));
  if (params?.dateFrom) queryParams.append('dateFrom', toLocalDateString(params.dateFrom));
  if (params?.dateTo) queryParams.append('dateTo', toLocalDateString(params.dateTo));
  if (params?.establishmentId) queryParams.append('establishmentId', params.establishmentId);
  params?.programIds?.forEach((id) => queryParams.append('programIds', id));
  params?.departmentCodes?.forEach((code) => queryParams.append('departmentCodes', code));

  const response = await apiClient.get(`/users/admins/export?${queryParams.toString()}`, {
    responseType: 'blob',
  });

  const blob = new Blob([response.data], { type: XLSX_MIME });
  const url = globalThis.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `admins-${new Date().toISOString().slice(0, 10)}.xlsx`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  globalThis.URL.revokeObjectURL(url);
};
